import { Close } from '@icon-park/react'
import { memo, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Tokens } from '@/assets/tokens'
import { Button, Selector, Switch } from '@/components'
import { HorizontalTagInput, TagOption } from '@/components/core'
import { useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { SearchResponse, ThemeResponse, UpdateThemeRequest } from '@shared/models'

import {
  DialogContainer,
  DialogOverlay,
  InfoRow,
  ModalContent,
  ModalFooter,
  ModalHeader,
  PremiseCounter,
  PremiseHeader,
  PremiseLabel,
  PremiseSection,
  PremiseTextArea,
  SwitchContainer,
  ThemeInfoSection,
} from './EditThemeTermsModal.styles'

interface EditThemeTermsModalProps {
  theme: ThemeResponse
  onClose: () => void
  onSuccess: () => void
}

export const EditThemeTermsModal = memo(Component)

function Component({ theme, onClose, onSuccess }: EditThemeTermsModalProps): JSX.Element {
  const { sendAlert } = Alert.Hook()
  const { companyID } = useRouting()

  const [termsTags, setTermsTags] = useState<TagOption[]>([])
  const [detectionPremises, setDetectionPremises] = useState<string>('')
  const [nonDetectionPremises, setNonDetectionPremises] = useState<string>('')
  const [originalTermsString, setOriginalTermsString] = useState<string>('')
  const [originalDetectionPremises, setOriginalDetectionPremises] = useState<string>('')
  const [originalNonDetectionPremises, setOriginalNonDetectionPremises] = useState<string>('')
  const [originalDeterministic, setOriginalDeterministic] = useState<boolean>(false)
  const [originalThemeGroupInternalID, setOriginalThemeGroupInternalID] = useState<string | undefined>()

  const deterministicBinder = Switch.Hook(theme.isStrictDeterministic || false)
  const { selectedOption: selectedThemeGroup, binder: themeGroupBinder } = Selector.Binder()

  const [isSearchingThemeGroups, searchThemeGroups, searchedThemeGroups = []] = Api.Query<SearchResponse[]>(
    `theme-groups/search?company=${companyID}&size=10000`,
  )

  const { isRunning: isUpdating, run: updateTheme } = Api.Command<UpdateThemeRequest>(Api.Methods.PATCH, 'themes/:id')
  const hasInitialized = useRef(false)

  useEffect(() => {
    hasInitialized.current = false
  }, [theme])

  useEffect(() => {
    const currentScrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${currentScrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      document.body.style.overflow = ''
      window.scrollTo(0, currentScrollY)
    }
  }, [theme])

  useEffect(() => {
    if (searchedThemeGroups.length == 0) searchThemeGroups()
  }, [theme])

  useEffect(() => {
    if (hasInitialized.current) return
    if (searchedThemeGroups.length === 0) return

    hasInitialized.current = true

    const initialTerms = theme.terms || ''
    const initialDetectionPremises = theme.detectionPremises || ''
    const initialNonDetectionPremises = theme.nonDetectionPremises || ''
    const initialDeterministic = theme.isStrictDeterministic || false

    setOriginalTermsString(initialTerms)
    setOriginalDetectionPremises(initialDetectionPremises)
    setOriginalNonDetectionPremises(initialNonDetectionPremises)
    setOriginalDeterministic(initialDeterministic)
    setDetectionPremises(initialDetectionPremises)
    setNonDetectionPremises(initialNonDetectionPremises)

    if (deterministicBinder.isChecked !== initialDeterministic) {
      deterministicBinder.toggle()
    }

    if (theme.groupName) {
      const currentGroup = searchedThemeGroups.find((g) => g.text === theme.groupName)
      if (currentGroup) {
        themeGroupBinder.onSelect({ value: currentGroup.value, text: currentGroup.text })
        setOriginalThemeGroupInternalID(String(currentGroup.value))
      }
    }

    if (initialTerms.trim()) {
      setTermsTags(
        initialTerms
          .split(';')
          .map((term, index) => ({ id: `${theme.id}-${index}`, text: term.trim() }))
          .filter((tag) => tag.text !== ''),
      )
    } else {
      setTermsTags([])
    }
  }, [theme, searchedThemeGroups])

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose])

  function validatePremises(premises: string): boolean {
    if (!premises.trim()) return true
    const premisesList = premises.split(';').filter((p) => p.trim().length > 0)
    return premisesList.length <= 5
  }

  function handleSave(): void {
    if (!deterministicBinder.isChecked) {
      if (!validatePremises(detectionPremises)) {
        sendAlert({
          type: 'error',
          title: 'Erro de validação',
          message: 'Premissas de detecção: máximo de 5 premissas permitidas',
        })
        return
      }

      if (!validatePremises(nonDetectionPremises)) {
        sendAlert({
          type: 'error',
          title: 'Erro de validação',
          message: 'Premissas de não detecção: máximo de 5 premissas permitidas',
        })
        return
      }
    }

    const newTermsString = termsTags
      .map((tag) => tag.text.trim())
      .filter((text) => text !== '')
      .join(';')

    const newDeterministic = deterministicBinder.isChecked
    const newDetectionPremises = newDeterministic ? '' : detectionPremises
    const newNonDetectionPremises = newDeterministic ? '' : nonDetectionPremises
    const newThemeGroupInternalID =
      selectedThemeGroup ? Number(String(selectedThemeGroup.value).split('_').at(-1)) : undefined

    if (
      newTermsString === originalTermsString &&
      newDeterministic === originalDeterministic &&
      newDetectionPremises === originalDetectionPremises &&
      newNonDetectionPremises === originalNonDetectionPremises &&
      newThemeGroupInternalID === originalThemeGroupInternalID
    ) {
      sendAlert({
        type: 'success',
        title: 'Nenhuma alteração',
        message: 'Nenhuma modificação foi realizada.',
      })
      onClose()
      return
    }

    const input: UpdateThemeRequest = {
      terms: newTermsString,
      isStrictDeterministic: newDeterministic,
      detectionPremises: newDetectionPremises,
      nonDetectionPremises: newNonDetectionPremises,
      themeGroupInternalID: newThemeGroupInternalID,
      companyID: theme.id.split('-')[0],
    }

    updateTheme({
      input,
      binds: [['id', theme.id]],
      onSuccess: () => {
        sendAlert({
          type: 'success',
          title: 'Tema atualizado',
          message: 'O tema foi atualizado com sucesso!',
        })
        onSuccess()
      },
    })
  }

  const detectionPremisesCount =
    detectionPremises.trim() ? detectionPremises.split(';').filter((p) => p.trim().length > 0).length : 0
  const nonDetectionPremisesCount =
    nonDetectionPremises.trim() ? nonDetectionPremises.split(';').filter((p) => p.trim().length > 0).length : 0

  const modalElement = document.getElementById('modal')
  const alertElement = document.getElementById('alert')

  if (!modalElement) {
    console.error('Modal element not found')
    return <></>
  }

  return createPortal(
    <DialogOverlay>
      <DialogContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h1>Editar Tema</h1>
          <Button.Root $variant="link" onClick={onClose}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </ModalHeader>

        <ModalContent>
          <ThemeInfoSection>
            <InfoRow>
              <span>Tema:</span>
              <span>{theme.value}</span>
            </InfoRow>
            <InfoRow>
              <span>Marca:</span>
              <span>{theme.brandName}</span>
            </InfoRow>
          </ThemeInfoSection>

          <Selector.Default
            {...themeGroupBinder}
            label="Grupo Temático"
            placeholder="Selecione o grupo temático"
            options={searchedThemeGroups}
            loading={isSearchingThemeGroups}
            disabled={isUpdating}
          />

          <HorizontalTagInput
            label="Termos (tags)"
            placeholder="Digite termos separados por ponto e vírgula (;)"
            maxCharacters={1024}
            onChange={setTermsTags}
            preSelectedTags={termsTags}
            showClearButton={true}
            showBooleanHelp={true}
            disabled={isUpdating}
          />

          <SwitchContainer>
            <Switch.Root
              label="Aplicar apenas classificação determinística para este tema"
              isChecked={deterministicBinder.isChecked}
              toggle={deterministicBinder.toggle}
              direction="row"
            />
          </SwitchContainer>

          {!deterministicBinder.isChecked && (
            <>
              <PremiseSection>
                <PremiseHeader>
                  <PremiseLabel>Identificar TEMA quando: (opcional)</PremiseLabel>
                  <PremiseCounter>{detectionPremisesCount}/5 premissas</PremiseCounter>
                </PremiseHeader>
                <PremiseTextArea
                  placeholder="Digite as premissas separadas por ponto e vírgula (;) para quando detectar o tema"
                  value={detectionPremises}
                  onChange={(e) => setDetectionPremises(e.target.value)}
                  disabled={isUpdating}
                />
              </PremiseSection>

              <PremiseSection>
                <PremiseHeader>
                  <PremiseLabel>Não identificar TEMA nos casos a seguir: (opcional)</PremiseLabel>
                  <PremiseCounter>{nonDetectionPremisesCount}/5 premissas</PremiseCounter>
                </PremiseHeader>
                <PremiseTextArea
                  placeholder="Digite as premissas separadas por ponto e vírgula (;) para quando NÃO detectar o tema"
                  value={nonDetectionPremises}
                  onChange={(e) => setNonDetectionPremises(e.target.value)}
                  disabled={isUpdating}
                />
              </PremiseSection>
            </>
          )}
        </ModalContent>

        <ModalFooter>
          <Button.Root $variant="secondary" onClick={onClose} disabled={isUpdating} style={{ minWidth: '100px' }}>
            <Button.Text value="Cancelar" />
          </Button.Root>
          <Button.Root
            $variant="primary"
            onClick={handleSave}
            $loading={isUpdating}
            disabled={isUpdating}
            style={{ minWidth: '100px' }}>
            <Button.Text value="Salvar" />
          </Button.Root>
        </ModalFooter>
      </DialogContainer>

      {alertElement && createPortal(<div style={{ position: 'relative', zIndex: 1 }}></div>, alertElement)}
    </DialogOverlay>,
    modalElement,
  )
}
